/**
 * BPF Cart Memorial Engraving — Phase 5.5.
 *
 * Extends the existing cart memorial preview (snippets/bpf-cart-
 * memorial-preview.liquid, live since Phase 3.0.2) with the same live
 * Vault engraving preview the Builder shows, in the AJAX cart drawer
 * and the full cart page. This file owns exactly one thing: finding
 * `[data-cart-engraving]` canvas elements and calling the EXISTING
 * renderer with data read from them — it contains no drawing logic,
 * no geometry math, and no color logic of its own. Every one of those
 * lives in the same three files the Builder already uses and does not
 * modify:
 *
 *   - assets/bpf-engraving-geometry.js  (layout/shrink-to-fit math)
 *   - assets/bpf-engraving-shadow.js    (Canvas 2D drawing/compositing)
 *   - assets/bpf-vault-engraving-layer.js (BpfVaultEngravingLayer —
 *     image loading, the Phase 5.3.2B CORS fix, the Vault-only/empty-
 *     name gates, generation-counter stale-load protection, and the
 *     "any error just hides the canvas" failure handling)
 *
 * This file constructs one BpfVaultEngravingLayer per cart canvas
 * (identical class, identical render() contract the Builder already
 * uses) and calls .render() with a canvasState object built from that
 * canvas element's own data-* attributes — themselves populated by
 * snippets/bpf-cart-memorial-preview.liquid purely from
 * item.properties/item.options_with_values already saved on the cart
 * line, the SAME source-of-truth that snippet already uses for the
 * base/figure images. No new hidden line-item properties were needed.
 *
 * WHEN this runs (the only genuinely new logic in this file):
 *   - Once on page load, for whatever cart items are already in the
 *     DOM (cart drawer markup is present sitewide; the full cart page
 *     has its own items on a normal page load).
 *   - Again, automatically, whenever the cart drawer or the full cart
 *     page's item list gets rebuilt by Shopify's existing AJAX flow
 *     (assets/cart-drawer.js's CartDrawer.renderContents() and
 *     assets/cart.js's CartItems.updateQuantity(), both of which
 *     replace a container's innerHTML with freshly rendered Liquid —
 *     including a fresh copy of this file's own markup with current
 *     data attributes). Neither of those files dispatches an event on
 *     completion, and editing them was deliberately avoided (this
 *     phase is rendering-only; those files own real cart behavior).
 *     A childList-only, non-attribute MutationObserver on the two
 *     known container ids is the narrowest way to detect "the DOM was
 *     just rebuilt" without touching them — it does not fire from this
 *     file's own work (drawing a canvas or toggling its `hidden`
 *     attribute is not a childList change), so there is no feedback
 *     loop, and it never fires on a timer — only on an actual
 *     replacement.
 *
 * Capsule: snippets/bpf-cart-memorial-preview.liquid emits a
 * [data-cart-engraving] canvas for a Capsule line only when that line's
 * own visual mapping record declares engravingSurface: 'capsule' (today,
 * only Dog Bone Capsule) — every other Capsule's canvas still exists in
 * markup (so renderOne() runs) but BpfVaultEngravingLayer.render() itself
 * rejects it (engravingSurface stays 'base', meaningless for a flattened
 * composition), the same "hide, don't guess" gate every other missing-
 * capability case already uses. No Capsule-specific rendering logic
 * lives in this file — see renderOne()'s isCapsule detection below for
 * the one new piece of data-shape branching this phase added.
 */

if (typeof window.BpfCartEngraving === 'undefined') {
  window.BpfCartEngraving = (function () {
    'use strict';

    // canvasEl -> BpfVaultEngravingLayer, so a canvas that survives
    // across two renderAll() calls (nothing rebuilt it) reuses its
    // already-constructed layer/shadow-renderer instead of rebuilding
    // one — a canvas removed by an AJAX rebuild simply drops out of
    // this map with no explicit cleanup needed (WeakMap + GC).
    const layers = new WeakMap();

    function renderOne(canvasEl) {
      if (typeof window.BpfVaultEngravingLayer === 'undefined') return;
      let layer = layers.get(canvasEl);
      if (!layer) {
        layer = new window.BpfVaultEngravingLayer(canvasEl);
        layers.set(canvasEl, layer);
      }

      const data = canvasEl.dataset;
      // Dog Bone Capsule phase: this canvas now comes from either of
      // snippets/bpf-cart-memorial-preview.liquid's two mutually-
      // exclusive branches — Vault's (data-vault-color/data-figure-
      // color/data-base-src/data-figure-src) or Capsule's (data-
      // capsule-color/data-capsule-src) — never both, since that
      // Liquid file's own bpf_supported/bpf_capsule_supported branches
      // are themselves mutually exclusive. 'capsuleColor' in data
      // reliably distinguishes them: the Vault branch never emits a
      // data-capsule-color attribute at all (genuinely absent from the
      // DOM, not just empty), matching the same detection the resolved
      // visual mapping record's own compositionMode already drives
      // server-side.
      const isCapsule = 'capsuleColor' in data;
      const vaultColor = data.vaultColor || '';
      const figureColor = data.figureColor || '';
      const capsuleColor = data.capsuleColor || '';
      // Exactly the canvasState shape assets/bpf-memorial-
      // designer.js's getCanvasState() already builds for the same
      // render() call — see that method's own comment for the field
      // meanings.
      const canvasState = {
        memorialStyle: isCapsule ? 'Capsule' : 'Vault',
        engravedName: data.engravedName || '',
        vaultColor,
        figureColor,
        capsuleColor,
        // Engraving Color feature: the FINAL, already-effective color
        // submitted at Add to Cart time (assets/bpf-memorial-
        // designer.js's <select> is always kept synced to either the
        // customer's explicit override or the resolved default before
        // submission — see that file's syncEngravingColorFromDefault()).
        // Read here as a top-level canvasState field, matching exactly
        // where assets/bpf-vault-engraving-layer.js's render() looks
        // for it (canvasState.engravingColorOverride, never inside
        // visualAssets) — so it always takes top precedence over
        // visualAssets.engravingColorDefault/figureColor/capsuleColor
        // below, the same "never recompute the default differently in
        // Cart" guarantee every other engraving field here already has.
        engravingColorOverride: data.engravingColor || '',
        visualAssets: {
          baseAssetsByColor: vaultColor && data.baseSrc ? { [vaultColor]: { src: data.baseSrc } } : {},
          // Dog Bone Vault fix: BpfVaultEngravingLayer needs this map
          // whenever engravingSurface is "figure" (below) — sourced
          // from the new data-figure-src attribute snippets/bpf-cart-
          // memorial-preview.liquid emits alongside the existing
          // data-figure-color, mirroring baseAssetsByColor's own shape.
          figureAssetsByColor: figureColor && data.figureSrc ? { [figureColor]: { src: data.figureSrc } } : {},
          // Dog Bone Capsule fix: mirrors baseAssetsByColor/
          // figureAssetsByColor's own shape — sourced from the new
          // data-capsule-src attribute, needed whenever engravingSurface
          // is "capsule".
          flattenedAssetsByColor: capsuleColor && data.capsuleSrc ? { [capsuleColor]: { src: data.capsuleSrc } } : {},
          // Dog Bone Vault phase: mirrors the Builder's own
          // visualAssets.engravingProfileRef (assets/bpf-memorial-
          // designer.js's getCanvasState()) so BpfVaultEngravingLayer
          // selects the same profile in the Cart as it would in the
          // Builder for the identical product. Sourced from the
          // data-engraving-profile attribute snippets/bpf-cart-
          // memorial-preview.liquid now emits; defaults to 'standard'
          // for any older/cached markup that predates this attribute.
          engravingProfileRef: data.engravingProfile || 'standard',
          // Dog Bone Vault fix + Dog Bone Capsule extension: mirrors
          // engravingProfileRef's sourcing — 'base' (default, unchanged
          // behavior), 'figure' (Dog Bone Vault), or 'capsule' (Dog Bone
          // Capsule), read from the new data-engraving-surface
          // attribute.
          engravingSurface: data.engravingSurface || 'base',
          // Dog Bone Vault refinement: mirrors engravingSurface's
          // sourcing — '' (default, unchanged behavior: engraving
          // color still follows figureColor/capsuleColor) or a literal
          // color like 'Black' (Dog Bone Vault, fixed regardless of
          // figure color), read from the new data-engraving-color-
          // default attribute. BpfVaultEngravingLayer.render() applies
          // the same precedence here it applies in the Builder.
          engravingColorDefault: data.engravingColorDefault || '',
        },
      };
      layer.render(canvasState);
    }

    function renderAll() {
      document.querySelectorAll('[data-cart-engraving]').forEach(renderOne);
    }

    // Coalesces near-simultaneous mutations (e.g. the drawer and the
    // cart icon bubble both updating from the same AJAX response) into
    // one renderAll() pass — not a polling loop, since it only ever
    // runs after an observer callback actually fires, never on its own
    // timer.
    let scheduled = null;
    function scheduleRenderAll() {
      if (scheduled) return;
      scheduled = setTimeout(() => {
        scheduled = null;
        renderAll();
      }, 50);
    }

    function observe(container) {
      if (!container) return;
      const observer = new MutationObserver(scheduleRenderAll);
      observer.observe(container, { childList: true, subtree: true });
    }

    function init() {
      renderAll();
      // Cart drawer: assets/cart-drawer.js replaces #CartDrawer's own
      // innerHTML (add-to-cart flow) or a descendant's (in-drawer
      // quantity changes) — observing #CartDrawer with subtree:true
      // catches both without needing to know which one happened.
      observe(document.getElementById('CartDrawer'));
      // Full cart page: assets/cart.js's CartItems.updateQuantity()
      // replaces the '.js-contents' descendant inside #main-cart-items
      // — only present on the cart page itself.
      observe(document.getElementById('main-cart-items'));
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }

    return { renderAll };
  })();
}
